import type { Review, ReviewRatingFilter, ReviewSort } from '../model/schema';

/**
 * Sorting and score-filtering the review list.
 *
 * **Both are CLIENT-SIDE, and that is a constraint rather than a choice.**
 * `GET /reviews/by-merchant` accepts `pagination.limit`/`pagination.offset`
 * and nothing else — no `sort`, no rating band (verified against the v1.00.184
 * spec and the live gateway). So the screen fetches one page and reorders it
 * here.
 *
 * The consequence is worth stating plainly, because it is invisible until a
 * merchant has many reviews: these operate over the FETCHED PAGE, not the whole
 * table. "С высокой оценкой" means "the 5-star reviews among the ones loaded".
 * `REVIEWS_PAGE_SIZE` is therefore set high enough to cover a realistic
 * merchant's history in one read — the honest fix is a server-side sort, and
 * this is the note for whoever adds it.
 */

/** 4 stars and up — the «С высокой оценкой» band. */
const HIGH_RATING_MIN = 4;
/** 2 stars and down — the «С низкой оценкой» band. A 3 lands in neither. */
const LOW_RATING_MAX = 2;

/** Narrow to one score band. `default` passes everything through. */
export function filterReviewsByRating(
  reviews: Review[],
  filter: ReviewRatingFilter,
): Review[] {
  if (filter === 'default') return reviews;
  if (filter === 'high') {
    return reviews.filter((review) => review.rating >= HIGH_RATING_MIN);
  }
  return reviews.filter((review) => review.rating <= LOW_RATING_MAX);
}

/**
 * Order the list by creation date.
 *
 * `default` returns the array UNTOUCHED — the server's own order, which is the
 * only ordering that is not a client-side guess. The other two sort a COPY, so
 * a cached query result is never mutated in place (React Query hands out the
 * cached array by reference; sorting it would reorder every other consumer's
 * view of the same data and defeat referential-equality re-render checks).
 *
 * A row with no parseable `createdAt` sorts last in both directions rather than
 * to whichever end `NaN` comparison happens to land it — an undated review is
 * not "the newest".
 */
export function sortReviews(reviews: Review[], sort: ReviewSort): Review[] {
  if (sort === 'default') return reviews;

  const direction = sort === 'newest' ? -1 : 1;
  return [...reviews].sort((a, b) => {
    const left = toTime(a.createdAt);
    const right = toTime(b.createdAt);
    if (left === null && right === null) return 0;
    if (left === null) return 1;
    if (right === null) return -1;
    return (left - right) * direction;
  });
}

function toTime(timestamp: string): number | null {
  if (!timestamp) return null;
  const time = new Date(timestamp).getTime();
  return Number.isNaN(time) ? null : time;
}
